import React, { useState, useEffect, useRef } from 'react';
import {
  FaUsers,
  FaMoneyBill,
  FaDownload,
  FaChevronLeft,
  FaChevronRight,
  FaTrash,
  FaUserEdit,
  FaSearch,
  FaFilter,
  FaTimes,
  FaCalendarAlt,
  FaUndo
} from 'react-icons/fa';
import { collection, getDocs, deleteDoc, doc, query, where, getDoc, writeBatch, updateDoc } from 'firebase/firestore';
import { db } from '../../config/firebaseConfig';
import * as XLSX from 'xlsx';
import styles from './SalaryPaymentPage.module.css';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import TableFilter from '../../components/TableFilter';
import { useTheme } from '../../contexts/ThemeContext';
import './SalaryPaymentDarkMode.css'; // Import dark mode styles

const truncateText = (text, maxLength = 15) => {
  if (
    text === null || 
    text === undefined || 
    text === '' || 
    Number.isNaN(text) ||
    (typeof text === 'string' && 
      ['nan', 'nat', 'n/a', 'null', 'undefined'].includes(text.toLowerCase()))
  ) {
    return '-';
  }
  const textStr = String(text);
  return textStr.length > maxLength 
    ? `${textStr.substring(0, maxLength)}...` 
    : textStr;
};

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('tr-TR', { 
    style: 'currency', 
    currency: 'TRY' 
  }).format(amount).replace('₺', '') + ' ₺';
};

const getCurrentYearMonth = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

const SalaryPaymentPage = () => {
  const { isDarkMode } = useTheme(); // Use the theme context
  const [salaries, setSalaries] = useState([]);
  const [selectedSalary, setSelectedSalary] = useState(null);
  const [filteredSalaries, setFilteredSalaries] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [filters, setFilters] = useState({});
  const [showLeftScroll, setShowLeftScroll] = useState(false);
  const [showRightScroll, setShowRightScroll] = useState(false);
  const tableContainerRef = useRef(null);
  const [selectedMonth, setSelectedMonth] = useState(getCurrentYearMonth());
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [currentVideo, setCurrentVideo] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showPaymentPreviewModal, setShowPaymentPreviewModal] = useState(false);
  const [paymentPreviewData, setPaymentPreviewData] = useState([]);
  const [showYolOdeModal, setShowYolOdeModal] = useState(false);
  const [yolUcretiAmount, setYolUcretiAmount] = useState('');
  const filtersRef = useRef(null);
  const [visibleColumns, setVisibleColumns] = useState({
    status: true,
    lastPaymentDate: true,
    employeeName: true,
    employeeSurname: true,
    grossSalary: true,
    sskEmployeeShare: true,
    unemploymentEmployeeShare: true,
    stampTax: true,
    totalDeductions: true,
    netSalary: true,
    sskEmployerShare: true,
    unemploymentEmployerShare: true,
    suggestedCost: true,
    contractDate: true
  });

  const columns = {
    status: { 
      label: 'Ödeme Durumu', 
      type: 'select',
      options: [
        { value: 'true', label: 'Ödendi' },
        { value: 'false', label: 'Ödenmedi' }
      ]
    },
    lastPaymentDate: { label: 'Son Ödeme Tarihi', type: 'date' },
    employeeName: { label: 'İsim', type: 'text' },
    employeeSurname: { label: 'Soyisim', type: 'text' },
    grossSalary: { label: 'Brüt Ücret', type: 'number' },
    sskEmployeeShare: { label: 'SGK İşçi Payı (%14)', type: 'number' },
    unemploymentEmployeeShare: { label: 'İşsizlik Sig. İşçi Payı (%1)', type: 'number' },
    stampTax: { label: 'Damga Vergisi', type: 'number' },
    totalDeductions: { label: 'Kesintiler Toplamı', type: 'number' },
    netSalary: { label: 'Net Ücret', type: 'number' },
    sskEmployerShare: { label: 'SGK İşveren Payı (%15.5)', type: 'number' },
    unemploymentEmployerShare: { label: 'İşsizlik Sig. İşveren Payı (%2)', type: 'number' },
    suggestedCost: { label: 'Toplam Maliyet', type: 'number' },
    contractDate: { label: 'Sözleşme Tarihi', type: 'date' }
  };

  const fetchSalaries = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'Salaries'));

      // Group salaries by employee and keep only the latest one
      const salariesByEmployee = {};
      querySnapshot.docs.forEach((docSnapshot) => {
        const salaryDoc = { id: docSnapshot.id, ...docSnapshot.data() };
        const employeeId = salaryDoc.employeeId;

        if (!salariesByEmployee[employeeId]) {
          salariesByEmployee[employeeId] = salaryDoc;
        } else {
          // Compare timestamps (updatedAt or createdAt) and keep the latest
          const currentTime = salariesByEmployee[employeeId].updatedAt ||
                             salariesByEmployee[employeeId].createdAt || 0;
          const newTime = salaryDoc.updatedAt || salaryDoc.createdAt || 0;

          if (newTime > currentTime) {
            salariesByEmployee[employeeId] = salaryDoc;
          }
        }
      });

      // Convert to array and fetch employee data
      const salaryData = await Promise.all(
        Object.values(salariesByEmployee).map(async (salaryDoc) => {
          const employeeRef = doc(db, 'Employees', salaryDoc.employeeId);
          const employeeSnap = await getDoc(employeeRef);

          const employeeData = employeeSnap.exists() ? employeeSnap.data() : {};

          const isSelectedMonthPaid = !!salaryDoc.payments?.[selectedMonth];

          return {
            ...salaryDoc,
            employeeName: employeeData.eName || '',
            employeeSurname: employeeData.eSurname || '',
            isCurrentMonthPaid: isSelectedMonthPaid,
            currentMonth: selectedMonth,
            lastPaymentDate: salaryDoc.payments?.[selectedMonth]?.paidDate || null
          };
        })
      );

      setSalaries(salaryData);
      setFilteredSalaries(salaryData);
    } catch (error) {
      console.error('Error fetching salaries:', error);
      toast.error('Maaş bilgileri alınırken bir hata oluştu');
    }
  };

  const fetchEmployees = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'Employees'));
      const employeeData = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        eName: doc.data().eName,
        eSurname: doc.data().eSurname
      }));
      setEmployees(employeeData);
    } catch (error) {
      console.error('Error fetching employees:', error);
      toast.error('Çalışan bilgileri alınırken bir hata oluştu');
    }
  };

  useEffect(() => {
    fetchSalaries();
    fetchEmployees();

    // Add click event listener to close filters dropdown when clicking outside
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [selectedMonth]);

  const handleClickOutside = (event) => {
    if (filtersRef.current && !filtersRef.current.contains(event.target)) {
      setShowFilters(false);
    }
  };

  useEffect(() => {
    let filtered = salaries;

    // Apply search term first (searches employee name and surname)
    if (searchTerm) {
      const lowercasedSearch = searchTerm.toLowerCase();
      filtered = filtered.filter(
        salary => 
          (salary.employeeName && salary.employeeName.toString().toLowerCase().includes(lowercasedSearch)) ||
          (salary.employeeSurname && salary.employeeSurname.toString().toLowerCase().includes(lowercasedSearch))
      );
    }

    // Then apply individual column filters
    Object.keys(filters).forEach(key => {
      if (filters[key]) {
        if (key === 'isCurrentMonthPaid') {
          filtered = filtered.filter(salary => 
            String(salary.isCurrentMonthPaid) === filters[key]
          );
        } else if (key.includes('.')) {
          const [parent, child] = key.split('.');
          filtered = filtered.filter(salary =>
            salary[parent]?.[child]?.toLowerCase().includes(filters[key].toLowerCase())
          );
        } else {
          filtered = filtered.filter(salary =>
            String(salary[key] || '')
              .toLowerCase()
              .includes(filters[key].toLowerCase())
          );
        }
      }
    });

    setFilteredSalaries(filtered);
  }, [salaries, filters, searchTerm]);

  const handleFilter = (column, value) => {
    const newFilters = { ...filters, [column]: value };
    setFilters(newFilters);
  };

  const clearFilters = () => {
    setFilters({});
    setSearchTerm('');
  };

  const toggleColumnVisibility = (column) => {
    setVisibleColumns(prev => ({
      ...prev,
      [column]: !prev[column]
    }));
  };

  const activeCount = salaries.filter(salary => salary.isCurrentMonthPaid).length;
  const inactiveCount = salaries.filter(salary => !salary.isCurrentMonthPaid).length;

  const handleShowPaymentPreview = () => {
    const unpaidSalaries = salaries.filter(salary => !salary.isCurrentMonthPaid);

    if (unpaidSalaries.length === 0) {
      toast.info('Bu ay için ödenecek maaş bulunmuyor');
      return;
    }

    // Calculate payment details for each unpaid salary
    const previewData = unpaidSalaries.map(salary => {
      // Ensure grossSalary is a number
      const grossSalary = parseFloat(salary.grossSalary) || 0;
      const sskEmployeeShare = grossSalary * 0.14;
      const unemploymentEmployeeShare = grossSalary * 0.01;
      const stampTax = grossSalary * 0.00759;
      const totalDeductions = sskEmployeeShare + unemploymentEmployeeShare + stampTax;
      const netSalary = grossSalary - totalDeductions;
      const sskEmployerShare = grossSalary * 0.155;
      const unemploymentEmployerShare = grossSalary * 0.02;
      const totalEmployerCost = grossSalary + sskEmployerShare + unemploymentEmployerShare;

      return {
        id: salary.id,
        employeeName: salary.employeeName,
        employeeSurname: salary.employeeSurname,
        grossSalary: grossSalary,
        netSalary: netSalary,
        totalDeductions: totalDeductions,
        totalEmployerCost: totalEmployerCost,
        sskEmployeeShare: sskEmployeeShare,
        unemploymentEmployeeShare: unemploymentEmployeeShare,
        stampTax: stampTax,
        sskEmployerShare: sskEmployerShare,
        unemploymentEmployerShare: unemploymentEmployerShare,
        additionalPayment: 0, // Ek ödeme
        additionalDeduction: 0 // Ek kesinti
      };
    });

    setPaymentPreviewData(previewData);
    setShowPaymentPreviewModal(true);
  };

  const handleAdditionalChange = (employeeId, field, value) => {
    const numValue = parseFloat(value) || 0;

    setPaymentPreviewData(prevData =>
      prevData.map(payment => {
        if (payment.id === employeeId) {
          const updatedPayment = { ...payment, [field]: numValue };

          // Recalculate net salary with additional payments and deductions
          const baseNetSalary = updatedPayment.grossSalary - updatedPayment.totalDeductions;
          updatedPayment.netSalary = baseNetSalary + updatedPayment.additionalPayment - updatedPayment.additionalDeduction;

          return updatedPayment;
        }
        return payment;
      })
    );
  };

  const handleYolOde = () => {
    setYolUcretiAmount('');
    setShowYolOdeModal(true);
  };

  const handleYolOdeConfirm = () => {
    const amount = parseFloat(yolUcretiAmount) || 0;
    if (amount <= 0) {
      setShowYolOdeModal(false);
      return;
    }

    setPaymentPreviewData(prevData =>
      prevData.map(payment => {
        const updatedPayment = { ...payment, additionalPayment: amount };
        // Recalculate net salary with the new additional payment
        const baseNetSalary = updatedPayment.grossSalary - updatedPayment.totalDeductions;
        updatedPayment.netSalary = baseNetSalary + updatedPayment.additionalPayment - updatedPayment.additionalDeduction;
        return updatedPayment;
      })
    );
    setShowYolOdeModal(false);
  };

  const handleBulkPayment = async () => {
    const batch = writeBatch(db);
    let updatedCount = 0;

    try {
      for (const paymentData of paymentPreviewData) {
        const salaryRef = doc(db, 'Salaries', paymentData.id);

        batch.update(salaryRef, {
          [`payments.${selectedMonth}`]: {
            paidDate: new Date().toISOString(),
            amount: paymentData.netSalary,
            grossAmount: paymentData.grossSalary,
            totalDeductions: paymentData.totalDeductions,
            suggestedCost: paymentData.totalEmployerCost,
            sskEmployeeShare: paymentData.sskEmployeeShare,
            unemploymentEmployeeShare: paymentData.unemploymentEmployeeShare,
            stampTax: paymentData.stampTax,
            sskEmployerShare: paymentData.sskEmployerShare,
            unemploymentEmployerShare: paymentData.unemploymentEmployerShare,
            additionalPayment: paymentData.additionalPayment || 0,
            additionalDeduction: paymentData.additionalDeduction || 0
          }
        });
        updatedCount++;
      }

      await batch.commit();
      toast.success(`${updatedCount} çalışanın maaş ödemesi tamamlandı`);
      setShowPaymentPreviewModal(false);
      setPaymentPreviewData([]);
      fetchSalaries();
    } catch (error) {
      console.error('Toplu ödeme sırasında hata:', error);
      toast.error('Ödeme sırasında bir hata oluştu');
    }
  };

  const handleSinglePayment = async (salary) => {
    if (salary.isCurrentMonthPaid) {
      toast.info(`${salary.employeeName} ${salary.employeeSurname} adlı çalışanın bu ayki maaşı zaten ödenmiş`);
      return;
    }

    try {
      const salaryRef = doc(db, 'Salaries', salary.id);

      const grossSalary = salary.grossSalary;
      const sskEmployeeShare = grossSalary * 0.14;
      const unemploymentEmployeeShare = grossSalary * 0.01;
      const stampTax = grossSalary * 0.00759;
      const totalDeductions = sskEmployeeShare + unemploymentEmployeeShare + stampTax;
      const netSalary = grossSalary - totalDeductions;
      const sskEmployerShare = grossSalary * 0.155;
      const unemploymentEmployerShare = grossSalary * 0.02;
      const totalEmployerCost = grossSalary + sskEmployerShare + unemploymentEmployerShare;

      await updateDoc(salaryRef, {
        [`payments.${selectedMonth}`]: {
          paidDate: new Date().toISOString(),
          amount: netSalary,
          grossAmount: grossSalary,
          totalDeductions: totalDeductions,
          suggestedCost: totalEmployerCost,
          sskEmployeeShare: sskEmployeeShare,
          unemploymentEmployeeShare: unemploymentEmployeeShare,
          stampTax: stampTax,
          sskEmployerShare: sskEmployerShare,
          unemploymentEmployerShare: unemploymentEmployerShare
        }
      });

      toast.success(`${salary.employeeName} ${salary.employeeSurname} adlı çalışanın maaş ödemesi tamamlandı`);
      fetchSalaries();
    } catch (error) {
      console.error('Ödeme sırasında hata:', error);
      toast.error('Ödeme sırasında bir hata oluştu');
    }
  };

  const handleReversePayment = async (salary) => {
    if (!salary.isCurrentMonthPaid) {
      toast.info(`${salary.employeeName} ${salary.employeeSurname} adlı çalışanın bu ayki maaşı zaten ödenmemiş`);
      return;
    }

    if (!window.confirm(`${salary.employeeName} ${salary.employeeSurname} adlı çalışanın ${selectedMonth} dönemi maaş ödemesini geri almak istediğinize emin misiniz?`)) {
      return;
    }

    try {
      const salaryRef = doc(db, 'Salaries', salary.id);

      // Get current salary data to preserve other payments
      const salaryDoc = await getDoc(salaryRef);
      const currentData = salaryDoc.data();
      const payments = { ...currentData.payments };

      // Delete the payment for the selected month
      delete payments[selectedMonth];

      await updateDoc(salaryRef, {
        payments: payments
      });

      toast.success(`${salary.employeeName} ${salary.employeeSurname} adlı çalışanın maaş ödemesi geri alındı`);
      fetchSalaries();
    } catch (error) {
      console.error('Ödeme geri alma sırasında hata:', error);
      toast.error('Ödeme geri alınırken bir hata oluştu');
    }
  };

  const handleDeleteSalary = async () => {
    if (!selectedSalary) return;

    try {
      await deleteDoc(doc(db, 'Salaries', selectedSalary.id));
      toast.success('Maaş başarıyla silindi');
      setSelectedSalary(null);
      fetchSalaries();
    } catch (error) {
      console.error('Error deleting salary:', error);
      toast.error('Maaş silinirken bir hata oluştu');
    }
  };

  const handleEditSalary = () => {
    if (selectedSalary) {
      // This would typically open a modal, but we'll keep it as is for now
      toast.info('Edit functionality not implemented in this example');
    }
  };

  const downloadExcel = () => {
    const dataToExport = salaries.map(salary => ({
      'Sözleşme Tarihi': salary.contractDate,
      'İsim': salary.employeeName,
      'Soyisim': salary.employeeSurname,
      'Brüt Maaş': salary.grossSalary,
      'Gelir Vergisi': salary.incomeTax || (salary.grossSalary * 0.15), // Default calculation
      'Sigorta Primi': salary.insurancePremium || (salary.grossSalary * 0.14 + salary.grossSalary * 0.01),
      'Önerilen Maliyet': salary.suggestedCost || (salary.grossSalary * 1.37), // Default calculation (gross + employer shares)
      'Toplam Net': salary.totalNet || (salary.grossSalary - (salary.grossSalary * 0.15 + salary.grossSalary * 0.14 + salary.grossSalary * 0.01)),
      'Toplam Maaş': salary.totalSalary || salary.grossSalary,
      'Ödeme Durumu': salary.isCurrentMonthPaid ? 'Ödendi' : 'Ödenmedi',
      'Son Ödeme Tarihi': salary.lastPaymentDate ? 
        new Date(salary.lastPaymentDate).toLocaleDateString('tr-TR') : '-'
    }));

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Salaries');
    XLSX.writeFile(workbook, 'MaasListesi.xlsx');
  };

  const handleScroll = () => {
    if (tableContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = tableContainerRef.current;
      setShowLeftScroll(scrollLeft > 0);
      setShowRightScroll(scrollLeft < scrollWidth - clientWidth);
    }
  };

  useEffect(() => {
    const container = tableContainerRef.current;
    if (container) {
      handleScroll();
      
      // Prevent wheel events from propagating beyond the table container
      const preventWheelPropagation = (e) => {
        // Check if the table actually needs scrolling
        const { scrollHeight, clientHeight, scrollWidth, clientWidth } = container;
        const hasVerticalScroll = scrollHeight > clientHeight;
        const hasHorizontalScroll = scrollWidth > clientWidth;
        
        // Only handle the event if there's something to scroll
        if (hasVerticalScroll || hasHorizontalScroll) {
          // Always prevent the wheel event from affecting the page
          e.stopPropagation();
          e.preventDefault();
          
          // Manually handle the scroll within the table container
          const scrollAmount = e.deltaY || e.deltaX;
          if (e.shiftKey || (!hasVerticalScroll && hasHorizontalScroll)) {
            // Horizontal scroll if shift is pressed or only horizontal scrolling is available
            container.scrollLeft += scrollAmount;
          } else {
            // Vertical scroll
            container.scrollTop += scrollAmount;
          }
        }
      };
      
      container.addEventListener('scroll', handleScroll);
      container.addEventListener('wheel', preventWheelPropagation, { passive: false });
      
      return () => {
        container.removeEventListener('scroll', handleScroll);
        container.removeEventListener('wheel', preventWheelPropagation);
      };
    }
  }, [salaries, filteredSalaries]);

  const openVideoModal = (videoUrl) => {
    setCurrentVideo(videoUrl);
    setShowVideoModal(true);
  };

  const scrollTable = (direction) => {
    if (tableContainerRef.current) {
      const scrollAmount = 300;
      const newScrollPosition = direction === 'left' 
        ? tableContainerRef.current.scrollLeft - scrollAmount
        : tableContainerRef.current.scrollLeft + scrollAmount;
      
      tableContainerRef.current.scrollTo({
        left: newScrollPosition,
        behavior: 'smooth'
      });
    }
  };

  // Apply dark mode class to document body
  useEffect(() => {
    // Apply dark mode to document body based on isDarkMode
    if (isDarkMode) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
    
    // Cleanup
    return () => {
      document.body.classList.remove('dark-mode');
    };
  }, [isDarkMode]);

  return (
    <div className={`${styles.pageContainer} ${isDarkMode ? 'dark-mode' : ''}`}>
      <div className={`${styles.container} ${isDarkMode ? 'dark-mode' : ''}`}>
        <header className={`${styles.header} ${isDarkMode ? 'dark-mode' : ''}`}>
          <div className={styles.headerContent}>
            <h1 className={styles.title}>
              <FaMoneyBill className={styles.headerIcon} />
              Maaş Yönetimi
            </h1>
            <div className={styles.headerButtons}>
              <button className={styles.downloadButton} onClick={downloadExcel}>
                <FaDownload className={styles.buttonIcon} /> Tabloyu İndir
              </button>
            </div>
          </div>
        </header>

        <div className={`${styles.dashboard} ${isDarkMode ? 'dark-mode' : ''}`}>
          <div className={styles.statsCards}>
            <div className={styles.card}>
              <div className={styles.cardContent}>
                <div className={styles.cardIcon}>
                  <FaUsers />
                </div>
                <div className={styles.cardInfo}>
                  <p className={styles.cardValue}>{salaries.length}</p>
                  <h3 className={styles.cardTitle}>Toplam Maaş Kaydı</h3>
                </div>
              </div>
            </div>
            
            <div className={`${styles.card} ${styles.activeCard}`}>
              <div className={styles.cardContent}>
                <div className={styles.cardIcon}>
                  <FaUsers />
                </div>
                <div className={styles.cardInfo}>
                  <p className={styles.cardValue}>{activeCount}</p>
                  <h3 className={styles.cardTitle}>Ödenen Maaş</h3>
                </div>
              </div>
            </div>
            
            <div className={`${styles.card} ${styles.inactiveCard}`}>
              <div className={styles.cardContent}>
                <div className={styles.cardIcon}>
                  <FaUsers />
                </div>
                <div className={styles.cardInfo}>
                  <p className={styles.cardValue}>{inactiveCount}</p>
                  <h3 className={styles.cardTitle}>Ödenmemiş Maaş</h3>
                </div>
              </div>
            </div>
          </div>

          <div className={styles.searchFilterContainer}>
            <div className={styles.monthSelectContainer}>
              <div className={styles.searchBar}>
                <FaCalendarAlt className={styles.searchIcon} />
                <input
                  type="month"
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                  className={styles.monthInput}
                />
                {selectedMonth !== getCurrentYearMonth() && (
                  <button 
                    className={styles.currentMonthButton} 
                    onClick={() => setSelectedMonth(getCurrentYearMonth())}
                  >
                    Güncel Aya Dön
                  </button>
                )}
              </div>
            </div>
            
            <div className={styles.searchActions}>
              <div className={styles.searchBar}>
                <FaSearch className={styles.searchIcon} />
                <input
                  type="text"
                  placeholder="İsim veya soyisim ile ara..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className={styles.searchInput}
                />
                {searchTerm && (
                  <button 
                    className={styles.clearSearch} 
                    onClick={() => setSearchTerm('')}
                  >
                    <FaTimes />
                  </button>
                )}
              </div>
              
              <div className={styles.filterContainer} ref={filtersRef}>
                <button 
                  className={styles.filterButton}
                  onClick={() => setShowFilters(!showFilters)}
                >
                  <FaFilter className={styles.filterIcon} />
                  <span>Filtreler</span>
                  {Object.keys(filters).length > 0 && (
                    <span className={styles.filterBadge}>
                      {Object.keys(filters).length}
                    </span>
                  )}
                </button>
                
                {showFilters && (
                  <div className={styles.filterDropdown}>
                    <div className={styles.filterHeader}>
                      <h3>Filtreler</h3>
                      <button 
                        className={styles.clearFiltersBtn}
                        onClick={clearFilters}
                      >
                        Tümünü Temizle
                      </button>
                    </div>
                    
                    <div className={styles.columnVisibility}>
                      <h4>Sütun Görünürlüğü</h4>
                      <div className={styles.columnCheckboxes}>
                        {Object.keys(columns).map(colKey => (
                          <div key={colKey} className={styles.columnCheckbox}>
                            <input
                              type="checkbox"
                              id={`col-${colKey}`}
                              checked={visibleColumns[colKey]}
                              onChange={() => toggleColumnVisibility(colKey)}
                            />
                            <label htmlFor={`col-${colKey}`}>
                              {columns[colKey].label}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {selectedSalary && (
          <div className={`${styles.actionPanel} ${isDarkMode ? 'dark-mode' : ''}`}>
            <div className={styles.selectedEmployeeInfo}>
              <h3 className={styles.selectedName}>
                {selectedSalary.employeeName} {selectedSalary.employeeSurname}
              </h3>
              <span className={`${styles.statusBadge} ${
                selectedSalary.isCurrentMonthPaid
                  ? styles.statusActive 
                  : styles.statusInactive
              }`}>
                {selectedSalary.isCurrentMonthPaid ? 'Ödendi' : 'Ödenmedi'}
              </span>
            </div>
            
            <div className={styles.actionButtons}>
              {selectedSalary.isCurrentMonthPaid ? (
                <button
                  className={`${styles.actionButton} ${styles.reversePaymentButton}`}
                  onClick={() => handleReversePayment(selectedSalary)}
                >
                  <FaUndo className={styles.actionIcon} />
                  <span>Geri Al</span>
                </button>
              ) : (
                <button
                  className={`${styles.actionButton} ${styles.singlePaymentButton}`}
                  onClick={() => handleSinglePayment(selectedSalary)}
                >
                  <FaMoneyBill className={styles.actionIcon} />
                  <span>Maaş Öde</span>
                </button>
              )}

              <button
                className={`${styles.actionButton} ${styles.editSalaryButton}`}
                onClick={handleEditSalary}
              >
                <FaUserEdit className={styles.actionIcon} />
                <span>Düzenle</span>
              </button>

              <button
                className={`${styles.actionButton} ${styles.deleteSalaryButton}`}
                onClick={handleDeleteSalary}
              >
                <FaTrash className={styles.actionIcon} />
                <span>Sil</span>
              </button>
            </div>
          </div>
        )}

        <div className={`${styles.bulkActionPanel} ${isDarkMode ? 'dark-mode' : ''} ${!inactiveCount ? styles.hidden : ''}`}>
          <button
            className={`${styles.bulkActionButton} ${styles.bulkPaymentButton}`}
            onClick={handleShowPaymentPreview}
            disabled={inactiveCount === 0}
          >
            <FaMoneyBill className={styles.actionIcon} />
            <span>Toplu Maaş Öde ({inactiveCount})</span>
          </button>
        </div>

        <section className={`${styles.tableSection} ${isDarkMode ? 'dark-mode' : ''}`}>
          <div className={`${styles.tableWrapper} ${isDarkMode ? 'dark-mode' : ''}`}>
            <div className={`${styles.tableScrollContainer} ${isDarkMode ? 'dark-mode' : ''}`}>
              {showLeftScroll && (
                <button
                  className={`${styles.scrollButton} ${styles.scrollLeft}`}
                  onClick={() => scrollTable('left')}
                >
                  <FaChevronLeft />
                </button>
              )}
              {showRightScroll && (
                <button
                  className={`${styles.scrollButton} ${styles.scrollRight}`}
                  onClick={() => scrollTable('right')}
                >
                  <FaChevronRight />
                </button>
              )}
              <div
                ref={tableContainerRef}
                className={`${styles.tableContainer} ${isDarkMode ? 'dark-mode' : ''}`}
                onScroll={handleScroll}
                onWheel={(e) => e.stopPropagation()}
              >
                <table className={`${styles.table} ${isDarkMode ? 'dark-mode' : ''}`}>
                  <thead>
                    <tr>
                      {Object.entries(columns).map(([key, col]) => 
                        visibleColumns[key] && (
                          <th key={key}>
                            {col.label}
                            <TableFilter 
                              column={key === 'status' ? 'isCurrentMonthPaid' : key}
                              type={col.type} 
                              options={col.options} 
                              onFilter={handleFilter}
                              currentValue={filters[key === 'status' ? 'isCurrentMonthPaid' : key] || ''} 
                            />
                          </th>
                        )
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredSalaries.length > 0 ? (
                      filteredSalaries.map((salary, index) => (
                        !salary.isEmpty && (
                          <tr 
                            key={salary.id} 
                            onClick={() => setSelectedSalary(salary)}
                            className={selectedSalary && selectedSalary.id === salary.id ? styles.selectedRow : ''}
                          >
                            {visibleColumns.status && (
                              <td>
                                <span className={`${styles.statusBadge} ${
                                  salary.isCurrentMonthPaid
                                    ? styles.statusActive
                                    : styles.statusInactive
                                }`}>
                                  {salary.isCurrentMonthPaid ? 'Ödendi' : 'Ödenmedi'}
                                </span>
                              </td>
                            )}
                            
                            {visibleColumns.lastPaymentDate && (
                              <td title={salary.lastPaymentDate ? new Date(salary.lastPaymentDate).toLocaleDateString('tr-TR') : '-'}>
                                {truncateText(salary.lastPaymentDate ? new Date(salary.lastPaymentDate).toLocaleDateString('tr-TR') : '-')}
                              </td>
                            )}
                            
                            {visibleColumns.employeeName && (
                              <td title={salary.employeeName}>{truncateText(salary.employeeName)}</td>
                            )}
                            
                            {visibleColumns.employeeSurname && (
                              <td title={salary.employeeSurname}>{truncateText(salary.employeeSurname)}</td>
                            )}
                            
                            {visibleColumns.grossSalary && (
                              <td title={formatCurrency(salary.grossSalary)}>{truncateText(formatCurrency(salary.grossSalary))}</td>
                            )}
                            
                            {visibleColumns.sskEmployeeShare && (
                              <td title={formatCurrency(salary.grossSalary * 0.14)}>{truncateText(formatCurrency(salary.grossSalary * 0.14))}</td>
                            )}
                            
                            {visibleColumns.unemploymentEmployeeShare && (
                              <td title={formatCurrency(salary.grossSalary * 0.01)}>{truncateText(formatCurrency(salary.grossSalary * 0.01))}</td>
                            )}
                            
                            {visibleColumns.stampTax && (
                              <td title={formatCurrency(salary.grossSalary * 0.00759)}>{truncateText(formatCurrency(salary.grossSalary * 0.00759))}</td>
                            )}
                            
                            {visibleColumns.totalDeductions && (
                              <td title={formatCurrency(salary.totalDeductions || (salary.grossSalary * 0.14 + salary.grossSalary * 0.01 + salary.grossSalary * 0.00759))}>
                                {truncateText(formatCurrency(salary.totalDeductions || (salary.grossSalary * 0.14 + salary.grossSalary * 0.01 + salary.grossSalary * 0.00759)))}
                              </td>
                            )}
                            
                            {visibleColumns.netSalary && (
                              <td title={formatCurrency(salary.netSalary || (salary.grossSalary - (salary.grossSalary * 0.14 + salary.grossSalary * 0.01 + salary.grossSalary * 0.00759)))}>
                                {truncateText(formatCurrency(salary.netSalary || (salary.grossSalary - (salary.grossSalary * 0.14 + salary.grossSalary * 0.01 + salary.grossSalary * 0.00759))))}
                              </td>
                            )}
                            
                            {visibleColumns.sskEmployerShare && (
                              <td title={formatCurrency(salary.grossSalary * 0.155)}>{truncateText(formatCurrency(salary.grossSalary * 0.155))}</td>
                            )}
                            
                            {visibleColumns.unemploymentEmployerShare && (
                              <td title={formatCurrency(salary.grossSalary * 0.02)}>{truncateText(formatCurrency(salary.grossSalary * 0.02))}</td>
                            )}
                            
                            {visibleColumns.suggestedCost && (
                              <td title={formatCurrency(salary.suggestedCost || (salary.grossSalary * 1.175))}>
                                {truncateText(formatCurrency(salary.suggestedCost || (salary.grossSalary * 1.175)))}
                              </td>
                            )}
                            
                            {visibleColumns.contractDate && (
                              <td title={salary.contractDate}>{truncateText(salary.contractDate)}</td>
                            )}
                          </tr>
                        )
                      ))
                    ) : (
                      <tr>
                        <td colSpan={Object.keys(visibleColumns).filter(key => visibleColumns[key]).length} className={styles.noDataCell}>
                          <div className={styles.noData}>
                            <FaUsers className={styles.noDataIcon} />
                            <p>Filtrelere uygun maaş kaydı bulunamadı</p>
                            {(Object.keys(filters).length > 0 || searchTerm) && (
                              <button className={styles.clearFiltersButton} onClick={clearFilters}>
                                Filtreleri Temizle
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </section>
      </div>

      {showVideoModal && (
        <div className={styles.modalBackdrop}>
          <div className={styles.videoModal}>
            <div className={styles.videoContent}>
              <video controls autoPlay>
                <source src={currentVideo} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
              <button className={styles.closeButton} onClick={() => setShowVideoModal(false)}>
                <FaTimes />
              </button>
            </div>
          </div>
        </div>
      )}

      {showPaymentPreviewModal && (
        <div className={styles.modalBackdrop} onClick={() => setShowPaymentPreviewModal(false)}>
          <div className={styles.paymentPreviewModal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>
                <FaMoneyBill className={styles.modalIcon} />
                Toplu Maaş Ödeme Önizleme
              </h2>
              <button
                className={styles.closeModalButton}
                onClick={() => setShowPaymentPreviewModal(false)}
              >
                <FaTimes />
              </button>
            </div>

            <div className={styles.modalContent}>
              <div className={styles.summarySection}>
                <div className={styles.summaryCard}>
                  <h3>Ödenecek Çalışan Sayısı</h3>
                  <p className={styles.summaryValue}>{paymentPreviewData.length}</p>
                </div>
                <div className={styles.summaryCard}>
                  <h3>Toplam Net Ödeme</h3>
                  <p className={styles.summaryValue}>
                    {formatCurrency(paymentPreviewData.reduce((sum, p) => sum + p.netSalary, 0))}
                  </p>
                </div>
                <div className={styles.summaryCard}>
                  <h3>Toplam Maliyet</h3>
                  <p className={styles.summaryValue}>
                    {formatCurrency(paymentPreviewData.reduce((sum, p) => sum + p.totalEmployerCost, 0))}
                  </p>
                </div>
              </div>

              <div className={styles.paymentDetailsTable}>
                <h3>Ödeme Detayları</h3>
                <div className={styles.tableScrollable}>
                  <table className={styles.previewTable}>
                    <thead>
                      <tr>
                        <th>Çalışan</th>
                        <th>Brüt Maaş</th>
                        <th>Kesintiler</th>
                        <th>Ek Ödeme</th>
                        <th>Ek Kesinti</th>
                        <th>Net Maaş</th>
                        <th>Toplam Maliyet</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paymentPreviewData.map((payment) => (
                        <tr key={payment.id}>
                          <td>
                            <strong>{payment.employeeName} {payment.employeeSurname}</strong>
                          </td>
                          <td>{formatCurrency(payment.grossSalary)}</td>
                          <td className={styles.deductionCell}>
                            {formatCurrency(payment.totalDeductions)}
                          </td>
                          <td>
                            <input
                              type="number"
                              className={styles.additionalInput}
                              value={payment.additionalPayment}
                              onChange={(e) => handleAdditionalChange(payment.id, 'additionalPayment', e.target.value)}
                              placeholder="0.00"
                              min="0"
                              step="0.01"
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              className={styles.additionalInput}
                              value={payment.additionalDeduction}
                              onChange={(e) => handleAdditionalChange(payment.id, 'additionalDeduction', e.target.value)}
                              placeholder="0.00"
                              min="0"
                              step="0.01"
                            />
                          </td>
                          <td className={styles.netSalaryCell}>
                            <strong>{formatCurrency(payment.netSalary)}</strong>
                          </td>
                          <td className={styles.costCell}>
                            {formatCurrency(payment.totalEmployerCost)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className={styles.totalRow}>
                        <td><strong>TOPLAM</strong></td>
                        <td><strong>{formatCurrency(paymentPreviewData.reduce((sum, p) => sum + p.grossSalary, 0))}</strong></td>
                        <td><strong>{formatCurrency(paymentPreviewData.reduce((sum, p) => sum + p.totalDeductions, 0))}</strong></td>
                        <td><strong className={styles.additionCell}>{formatCurrency(paymentPreviewData.reduce((sum, p) => sum + p.additionalPayment, 0))}</strong></td>
                        <td><strong className={styles.deductionCell}>{formatCurrency(paymentPreviewData.reduce((sum, p) => sum + p.additionalDeduction, 0))}</strong></td>
                        <td><strong>{formatCurrency(paymentPreviewData.reduce((sum, p) => sum + p.netSalary, 0))}</strong></td>
                        <td><strong>{formatCurrency(paymentPreviewData.reduce((sum, p) => sum + p.totalEmployerCost, 0))}</strong></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            </div>

            <div className={styles.modalFooter}>
              <button
                className={styles.cancelButton}
                onClick={() => setShowPaymentPreviewModal(false)}
              >
                İptal
              </button>
              <button
                className={styles.yolOdeButton}
                onClick={handleYolOde}
              >
                Yol Öde
              </button>
              <button
                className={styles.confirmPaymentButton}
                onClick={handleBulkPayment}
              >
                <FaMoneyBill />
                Ödemeyi Onayla ve Tamamla
              </button>
            </div>
          </div>
        </div>
      )}

      {showYolOdeModal && (
        <div className={styles.modalBackdrop} onClick={() => setShowYolOdeModal(false)}>
          <div className={styles.yolOdeModal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.yolOdeModalHeader}>
              <h3>Yol Ücreti Belirleme</h3>
              <button
                className={styles.closeModalButton}
                onClick={() => setShowYolOdeModal(false)}
              >
                <FaTimes />
              </button>
            </div>
            <div className={styles.yolOdeModalContent}>
              <label htmlFor="yolUcreti">Yol ücreti ne kadar olacak?</label>
              <input
                type="number"
                id="yolUcreti"
                className={styles.yolUcretiInput}
                value={yolUcretiAmount}
                onChange={(e) => setYolUcretiAmount(e.target.value)}
                placeholder="Tutar giriniz..."
                min="0"
                step="0.01"
                autoFocus
              />
            </div>
            <div className={styles.yolOdeModalFooter}>
              <button
                className={styles.cancelButton}
                onClick={() => setShowYolOdeModal(false)}
              >
                İptal
              </button>
              <button
                className={styles.yolOdeConfirmButton}
                onClick={handleYolOdeConfirm}
              >
                Uygula
              </button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme={isDarkMode ? "dark" : "light"}
      />
    </div>
  );
};

export default SalaryPaymentPage;